import React, { useState, useEffect } from 'react';
import { 
  FileText, Eye, Download, Users, TrendingUp, Palette, Plus, 
  Edit3, Trash2, Check, X, Search, RefreshCw, Settings, AlertCircle
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const BioDataAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [biodatas, setBiodatas] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('stats'); // stats, list, templates, limits
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Limits state
  const [limits, setLimits] = useState({
    free: 1,
    activated: 3,
    premium: 10,
    admin: 999
  });
  const [limitsStats, setLimitsStats] = useState(null);
  const [savingLimits, setSavingLimits] = useState(false);
  
  const [templateForm, setTemplateForm] = useState({
    name: '',
    slug: '',
    description: '',
    style: 'royal',
    is_active: true,
    is_premium: false,
    colors: {
      primary: '#D4AF37',
      secondary: '#1a365d',
      background: '#FDF5E6',
      text: '#1a1a1a',
      accent: '#ffd700',
      border: '#D4AF37'
    }
  });

  useEffect(() => {
    fetchStats();
    fetchBiodatas();
    fetchTemplates();
    fetchLimits();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/biodata/admin/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchBiodatas = async () => {
    try {
      const res = await api.get('/biodata/admin/all');
      setBiodatas(res.data.biodatas || []);
    } catch (error) {
      console.error('Failed to fetch biodatas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/biodata/admin/templates');
      setTemplates(res.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchLimits = async () => {
    try {
      const [limitsRes, statsRes] = await Promise.all([
        api.get('/biodata/admin/limits'),
        api.get('/biodata/admin/limits/stats')
      ]);
      setLimits(limitsRes.data.limits || { free: 1, activated: 3, premium: 10, admin: 999 });
      setLimitsStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch limits:', error);
    }
  };

  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      await api.put('/biodata/admin/limits', limits);
      toast.success('Limits updated successfully');
      fetchLimits();
    } catch (error) {
      toast.error('Failed to update limits');
    } finally {
      setSavingLimits(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.slug) {
      toast.error('Name and slug are required');
      return;
    }

    try {
      if (editingTemplate) {
        await api.put(`/biodata/admin/templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/biodata/admin/templates', templateForm);
        toast.success('Template created');
      }
      fetchTemplates();
      setShowTemplateModal(false);
      resetTemplateForm();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return;
    try {
      await api.delete(`/biodata/admin/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      slug: template.slug,
      description: template.description || '',
      style: template.style || 'royal',
      is_active: template.is_active !== false,
      is_premium: template.is_premium || false,
      colors: template.colors || {
        primary: '#D4AF37',
        secondary: '#1a365d',
        background: '#FDF5E6',
        text: '#1a1a1a',
        accent: '#ffd700',
        border: '#D4AF37'
      }
    });
    setShowTemplateModal(true);
  };

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      slug: '',
      description: '',
      style: 'royal',
      is_active: true,
      is_premium: false,
      colors: {
        primary: '#D4AF37',
        secondary: '#1a365d',
        background: '#FDF5E6',
        text: '#1a1a1a',
        accent: '#ffd700',
        border: '#D4AF37'
      }
    });
  };

  const filteredBiodatas = biodatas.filter(b => 
    b.personal_info?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6" data-testid="biodata-admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
              <FileText className="w-8 h-8 text-yellow-500" />
              Bio Data Admin
            </h1>
            <p className="text-muted-foreground mt-1">Manage biodatas and templates</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { fetchStats(); fetchBiodatas(); fetchTemplates(); }}
              className="px-3 py-2 bg-white/10 rounded-lg hover:bg-white/20"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'stats', label: 'Statistics', icon: TrendingUp },
            { id: 'list', label: `All Biodatas (${biodatas.length})`, icon: FileText },
            { id: 'templates', label: `Templates (${templates.length})`, icon: Palette },
            { id: 'limits', label: 'Member Limits', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-all ${
                activeTab === tab.id ? 'bg-yellow-500 text-black font-medium' : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="glass-card p-6 text-center">
              <FileText className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
              <p className="text-3xl font-bold">{stats.total_biodatas}</p>
              <p className="text-muted-foreground text-sm">Total Biodatas</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Check className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="text-3xl font-bold">{stats.published_biodatas}</p>
              <p className="text-muted-foreground text-sm">Published</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Eye className="w-10 h-10 text-blue-500 mx-auto mb-3" />
              <p className="text-3xl font-bold">{stats.total_views}</p>
              <p className="text-muted-foreground text-sm">Total Views</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Download className="w-10 h-10 text-purple-500 mx-auto mb-3" />
              <p className="text-3xl font-bold">{stats.total_downloads}</p>
              <p className="text-muted-foreground text-sm">Downloads</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Palette className="w-10 h-10 text-orange-500 mx-auto mb-3" />
              <p className="text-3xl font-bold">{stats.template_count}</p>
              <p className="text-muted-foreground text-sm">Templates</p>
            </div>
          </div>
        )}

        {/* List Tab */}
        {activeTab === 'list' && (
          <div>
            {/* Search */}
            <div className="mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or username..."
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>

            {/* List */}
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Name</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">User</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Template</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Views</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Downloads</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Created</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBiodatas.map((bio) => (
                      <tr key={bio.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3 font-medium">{bio.personal_info?.name || 'Untitled'}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">@{bio.username}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-500 rounded-full capitalize">
                            {bio.template}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            bio.is_published ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {bio.is_published ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{bio.view_count || 0}</td>
                        <td className="px-4 py-3 text-sm">{bio.download_count || 0}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(bio.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          {bio.is_published && (
                            <a
                              href={`/biodata/${bio.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs hover:bg-blue-500/30"
                            >
                              View
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredBiodatas.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  No biodatas found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            {/* Add Template Button */}
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => {
                  resetTemplateForm();
                  setShowTemplateModal(true);
                }}
                className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium flex items-center gap-2 hover:bg-yellow-400"
              >
                <Plus className="w-4 h-4" />
                Add Template
              </button>
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <div key={template.id} className="glass-card p-4 hover:ring-2 hover:ring-yellow-500/30 transition-all">
                  {/* Preview */}
                  <div 
                    className="h-32 rounded-lg mb-4 flex items-center justify-center"
                    style={{ 
                      backgroundColor: template.colors?.background || '#FDF5E6',
                      border: `2px solid ${template.colors?.border || '#D4AF37'}`
                    }}
                  >
                    <div className="text-center">
                      <div 
                        className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center text-2xl"
                        style={{ backgroundColor: (template.colors?.primary || '#D4AF37') + '30' }}
                      >
                        🙏
                      </div>
                      <span 
                        className="font-bold text-lg"
                        style={{ color: template.colors?.primary || '#D4AF37' }}
                      >
                        {template.name}
                      </span>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold">{template.name}</h3>
                      <p className="text-sm text-muted-foreground">{template.slug}</p>
                    </div>
                    <div className="flex gap-1">
                      {template.is_premium && (
                        <span className="px-2 py-1 text-[10px] bg-purple-500/20 text-purple-400 rounded-full">PREMIUM</span>
                      )}
                      <span className={`px-2 py-1 text-[10px] rounded-full ${
                        template.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {template.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{template.description}</p>

                  {/* Color Preview */}
                  <div className="flex gap-1 mb-4">
                    {Object.entries(template.colors || {}).map(([key, color]) => (
                      <div 
                        key={key}
                        className="w-6 h-6 rounded-full border border-white/20"
                        style={{ backgroundColor: color }}
                        title={key}
                      />
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => editTemplate(template)}
                      className="flex-1 px-3 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20 flex items-center justify-center gap-1"
                    >
                      <Edit3 className="w-4 h-4" /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Limits Tab */}
        {activeTab === 'limits' && (
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="glass-card p-4 bg-blue-500/10 border-blue-500/30">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-400">Biodata Creation Limits</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Control how many biodatas each user tier can create. Changes apply immediately to all users.
                  </p>
                </div>
              </div>
            </div>

            {/* Limits Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { key: 'free', label: 'Free Members', description: 'Users without activation', color: 'gray', icon: Users },
                { key: 'activated', label: 'Activated Members', description: 'Users with active package', color: 'blue', icon: Check },
                { key: 'premium', label: 'Premium Members', description: 'Premium subscription users', color: 'purple', icon: TrendingUp },
                { key: 'admin', label: 'Admins', description: 'Admin users', color: 'yellow', icon: Settings }
              ].map(tier => {
                const tierStats = limitsStats?.stats?.[tier.key] || {};
                return (
                  <div key={tier.key} className="glass-card p-4 hover:ring-2 hover:ring-white/10 transition-all">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-lg bg-${tier.color}-500/20`}>
                        <tier.icon className={`w-5 h-5 text-${tier.color}-400`} />
                      </div>
                      <div>
                        <h4 className="font-bold">{tier.label}</h4>
                        <p className="text-xs text-muted-foreground">{tier.description}</p>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm mb-1 text-muted-foreground">Max Biodatas</label>
                      <input
                        type="number"
                        min="0"
                        max="999"
                        value={limits[tier.key]}
                        onChange={(e) => setLimits(prev => ({ ...prev, [tier.key]: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xl font-bold text-center"
                      />
                    </div>

                    {/* Stats */}
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Users</span>
                        <span className="font-medium">{tierStats.total_users || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Biodatas Created</span>
                        <span className="font-medium">{tierStats.total_biodatas || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">At Limit</span>
                        <span className={`font-medium ${tierStats.users_at_limit > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                          {tierStats.users_at_limit || 0} users
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveLimits}
                disabled={savingLimits}
                className="px-6 py-3 bg-yellow-500 text-black rounded-xl font-medium hover:bg-yellow-400 flex items-center gap-2 disabled:opacity-50"
              >
                {savingLimits ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Limits
                  </>
                )}
              </button>
            </div>

            {/* Usage Summary */}
            {limitsStats && (
              <div className="glass-card p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-yellow-500" />
                  Usage Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-yellow-400">
                      {Object.values(limitsStats.stats || {}).reduce((sum, s) => sum + (s.total_users || 0), 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-blue-400">
                      {Object.values(limitsStats.stats || {}).reduce((sum, s) => sum + (s.total_biodatas || 0), 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Biodatas</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-orange-400">
                      {Object.values(limitsStats.stats || {}).reduce((sum, s) => sum + (s.users_at_limit || 0), 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Users at Limit</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-green-400">
                      {(Object.values(limitsStats.stats || {}).reduce((sum, s) => sum + (s.total_biodatas || 0), 0) / 
                        Math.max(1, Object.values(limitsStats.stats || {}).reduce((sum, s) => sum + (s.total_users || 0), 0))).toFixed(1)}
                    </p>
                    <p className="text-sm text-muted-foreground">Avg per User</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowTemplateModal(false)}>
          <div className="bg-[#1a1a1a] rounded-2xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5 text-yellow-500" />
              {editingTemplate ? 'Edit Template' : 'Add Template'}
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Name *</label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="Gold Royal"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Slug * (unique ID)</label>
                  <input
                    type="text"
                    value={templateForm.slug}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="gold-royal"
                    disabled={!!editingTemplate}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">Description</label>
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                  rows={2}
                  placeholder="Traditional gold and cream design..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Style</label>
                  <select
                    value={templateForm.style}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, style: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                  >
                    <option value="royal">Royal</option>
                    <option value="classic">Classic</option>
                    <option value="modern">Modern</option>
                    <option value="minimal">Minimal</option>
                  </select>
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={templateForm.is_active}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    Active
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={templateForm.is_premium}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, is_premium: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    Premium
                  </label>
                </div>
              </div>

              {/* Colors */}
              <div>
                <label className="block text-sm mb-2 font-medium">Colors</label>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(templateForm.colors).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-xs text-muted-foreground mb-1 capitalize">{key}</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={value}
                          onChange={(e) => setTemplateForm(prev => ({
                            ...prev,
                            colors: { ...prev.colors, [key]: e.target.value }
                          }))}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => setTemplateForm(prev => ({
                            ...prev,
                            colors: { ...prev.colors, [key]: e.target.value }
                          }))}
                          className="flex-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-xs"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm mb-2 font-medium">Preview</label>
                <div 
                  className="h-32 rounded-lg flex items-center justify-center"
                  style={{ 
                    backgroundColor: templateForm.colors.background,
                    border: `3px solid ${templateForm.colors.border}`
                  }}
                >
                  <div className="text-center">
                    <div 
                      className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center text-2xl"
                      style={{ backgroundColor: templateForm.colors.primary + '30' }}
                    >
                      🙏
                    </div>
                    <span 
                      className="font-bold"
                      style={{ color: templateForm.colors.primary }}
                    >
                      {templateForm.name || 'Template Name'}
                    </span>
                    <p className="text-sm mt-1" style={{ color: templateForm.colors.text }}>Sample Text</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="flex-1 px-4 py-3 bg-white/10 rounded-lg font-medium hover:bg-white/20"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                className="flex-1 px-4 py-3 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-400"
              >
                {editingTemplate ? 'Update' : 'Create'} Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BioDataAdmin;
